/**
 * @fileoverview Raw property field extraction for QMA + hooks (no formatting).
 * Shared by PropertyDetails and PropertyDetail flows.
 * @module utils/extractPropertyCoreFields
 */

import { DEFAULT_NOT_AVAILABLE, DEFAULT_UNKNOWN } from "@/constants/propertyFieldDefaults";
import { isEmpty, isString, toString as coerceToString, trim } from "@/utils/stringUtils";

const extractString = (value, defaultValue = "") => {
  const raw = value ?? "";
  const trimmed = trim(isString(raw) ? raw : coerceToString(raw));
  return isEmpty(trimmed) ? defaultValue : trimmed;
};

const extractNumber = (obj, path) => {
  const value = path.split(".").reduce((acc, key) => acc?.[key], obj);
  return Number.isFinite(Number(value)) ? Number(value) : 0;
};

/**
 * Extracts raw property details from a property object (unformatted values).
 *
 * @param {object|null|undefined} property - Property record from API/store
 * @returns {object} address, price, sqft, beds, baths, status, qmaReport, etc.
 */
export const extractPropertyDetails = (property) => {
  if (!property || typeof property !== "object") {
    return {
      address: "",
      price: 0,
      sqft: 0,
      beds: 0,
      baths: 0,
      status: DEFAULT_NOT_AVAILABLE,
      propertyType: DEFAULT_UNKNOWN,
      yearBuilt: DEFAULT_UNKNOWN,
      city: DEFAULT_UNKNOWN,
      state: "",
      zipcode: "",
      county: DEFAULT_UNKNOWN,
      customerId: "",
      hiddenAddress: false,
      qmaReport: "",
    };
  }

  const attrs = property.additional_attributes || {};

  return {
    address: extractString(property.full_address, ""),
    price: extractNumber(attrs, "listing_price"),
    sqft: extractNumber(attrs, "square_feet"),
    beds: extractNumber(attrs, "bedrooms"),
    baths: extractNumber(attrs, "bathrooms"),
    status: extractString(attrs.home_status, DEFAULT_NOT_AVAILABLE),
    propertyType: extractString(attrs.property_type, DEFAULT_UNKNOWN),
    yearBuilt: extractString(attrs.year_built, DEFAULT_UNKNOWN),
    city: extractString(property.city, DEFAULT_UNKNOWN),
    state: extractString(property.state, ""),
    zipcode: extractString(property.zipcode, ""),
    county: extractString(property.county, DEFAULT_UNKNOWN),
    customerId: extractString(property.customer, ""),
    hiddenAddress: Boolean(property.hidden_address),
    qmaReport: extractString(property.qma_report ?? attrs.qma_report, ""),
  };
};
